import { ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { palettes, tokens } from '../theme/tokens';
import { FlexGap } from './FlexGap';
import { Spinner } from './Spinner';
import { Paragraph } from './Text';

export const CYCLE_MOMENT_BAR_SIZE = 32;
export const CYCLE_MOMENT_ROW_HEIGHT = tokens.padding.compact * 2 + CYCLE_MOMENT_BAR_SIZE;

export type CycleMomentBarStatus = 'upcoming' | 'due' | 'completed';

function toTime(value: Date | string | number): number {
	if (value instanceof Date) {
		return value.getTime();
	}

	return new Date(value).getTime();
}

export function cycleMomentPlacement(
	bounds: [Date, Date],
	startDate: Date,
	endDate: Date,
): { leftPercent: number; widthPercent: number; clippedStart: boolean; clippedEnd: boolean } {
	const origin = toTime(bounds[0]);
	const range = toTime(bounds[1]) - origin;
	if (range <= 0) {
		return { leftPercent: 0, widthPercent: 0, clippedStart: false, clippedEnd: false };
	}

	const start = toTime(startDate) - origin;
	const end = toTime(endDate) - origin;
	const clippedStart = start < 0;
	const clippedEnd = end > range;
	const left = Math.max(0, start);
	const right = Math.min(range, end);

	return {
		leftPercent: (left / range) * 100,
		widthPercent: Math.max(0, ((right - left) / range) * 100),
		clippedStart,
		clippedEnd,
	};
}

export function CycleMomentOffset({
	bounds,
	startDate,
	endDate,
	children,
	minWidth,
	height,
}: {
	bounds: [Date, Date];
	startDate: Date;
	endDate: Date;
	children: ReactNode;
	minWidth?: number;
	height?: number | string;
}) {
	const { leftPercent, widthPercent } = cycleMomentPlacement(bounds, startDate, endDate);

	return (
		<div
			style={{
				position: 'relative',
				left: `${leftPercent}%`,
				width: `${widthPercent}%`,
				minWidth: minWidth ?? 40,
				height,
			}}
		>
			{children}
		</div>
	);
}

const fadeMask = (clippedStart?: boolean, clippedEnd?: boolean) => {
	if (!clippedStart && !clippedEnd) {
		return '';
	}

	const fade = `${tokens.padding.container}px`;
	const image =
		clippedStart && clippedEnd
			? `linear-gradient(to right, transparent, #000 ${fade}, #000 calc(100% - ${fade}), transparent)`
			: clippedStart
				? `linear-gradient(to right, transparent, #000 ${fade})`
				: `linear-gradient(to right, #000 calc(100% - ${fade}), transparent)`;

	return `
		mask-image: ${image};
		-webkit-mask-image: ${image};
	`;
};

const BarShell = styled.div`
	position: relative;
	width: 100%;
`;

const Bar = styled.div<{
	$status: CycleMomentBarStatus;
	$clippedStart?: boolean;
	$clippedEnd?: boolean;
}>`
	width: 100%;
	height: ${CYCLE_MOMENT_BAR_SIZE}px;
	display: flex;
	align-items: center;
	justify-content: flex-start;
	box-sizing: border-box;
	padding: 0 ${tokens.padding.compact}px;
	border-radius: ${tokens.radius.xs}px;
	border: 1px solid ${tokens.border.default};
	background: ${palettes.heatherGray['50']};
	color: ${tokens.icon.secondary};
	${(props) => fadeMask(props.$clippedStart, props.$clippedEnd)}

	svg {
		width: 20px;
		height: 20px;
		display: block;
		flex-shrink: 0;
		color: inherit;
		fill: none;
	}

	${(props) =>
		props.$status === 'completed' &&
		`
			color: ${tokens.success.icon};
			background: ${tokens.success.background};
			border-color: ${tokens.success.border};
	`}

	${(props) =>
		props.$status === 'due' &&
		`
			color: ${tokens.error.icon};
			background: ${tokens.error.background};
			border-color: ${tokens.error.border};
	`}
`;

const Title = styled.span<{ $muted?: boolean }>`
	position: absolute;
	left: 100%;
	top: 50%;
	transform: translateY(-50%);
	margin-left: ${tokens.gap.element}px;
	white-space: nowrap;
	color: ${(props) => (props.$muted ? tokens.text.tertiary : tokens.text.primary)};
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.body}px;
	font-weight: ${tokens.typography.weights.medium};
	letter-spacing: 0.01em;
	line-height: ${tokens.typography.lineHeights.body};
`;

export function CycleMomentBar({
	status,
	icon,
	title,
	clippedStart,
	clippedEnd,
}: {
	status: CycleMomentBarStatus;
	icon: ReactNode;
	title: string;
	clippedStart?: boolean;
	clippedEnd?: boolean;
}) {
	return (
		<BarShell>
			<Bar $status={status} $clippedStart={clippedStart} $clippedEnd={clippedEnd}>
				{icon}
			</Bar>
			<Title $muted={status === 'completed'}>{title}</Title>
		</BarShell>
	);
}

export type CycleMomentItem = {
	id: string;
	startDate: Date;
	endDate: Date;
	title: string;
	status: CycleMomentBarStatus;
	icon: ReactNode;
};

const Card = styled.div<{ $mini?: boolean; $embedded?: boolean }>`
	${(props) =>
		props.$mini || props.$embedded
			? `
		position: relative;
		overflow: hidden;
	`
			: `
		border: 1px solid ${tokens.border.subtle};
		background: ${tokens.surface.white};
		border-radius: ${tokens.radius.md}px;
		box-shadow: ${tokens.shadow.xs};
		padding: ${tokens.padding.container}px;
		display: flex;
		flex-direction: column;
		gap: ${tokens.gap.list}px;
		position: relative;
		overflow: hidden;
	`}
`;

const Timeline = styled.div`
	position: relative;
	width: 100%;
	display: flex;
	flex-direction: column;
`;

const TrackSection = styled.div`
	position: relative;
	z-index: 1;
	width: 100%;
	overflow: visible;
`;

const TodayLabelRow = styled.div`
	position: relative;
	width: 100%;
	height: calc(${tokens.typography.sizes.small}px * ${tokens.typography.lineHeights.small});
	margin-bottom: ${tokens.gap.inline}px;
`;

const Rows = styled.div<{ $fade: boolean }>`
	position: relative;
	padding-bottom: ${tokens.padding.compact}px;

	${(props) =>
		props.$fade &&
		`
	&:after {
		content: '';
		width: ${tokens.padding.container}px;
		display: block;
		position: absolute;
		z-index: 1;
		top: 0;
		right: 0;
		bottom: 0;
		pointer-events: none;
		background-image: linear-gradient(to right, rgba(255, 255, 255, 0), ${tokens.surface.white});
	}
	`}
`;

const Overlay = styled.div`
	position: absolute;
	inset: 0;
	z-index: 2;
`;

const MomentRow = styled.div`
	position: relative;
	box-sizing: border-box;
	width: 100%;
	height: ${CYCLE_MOMENT_ROW_HEIGHT}px;
	padding: ${tokens.padding.compact}px 0;
	box-shadow: inset 0 -1px 0 ${tokens.border.subtle};
`;

const Track = styled.div`
	width: 100%;
	height: 3px;
	position: relative;
	overflow: visible;
	display: flex;
	align-items: flex-end;

	&:before {
		content: '';
		position: absolute;
		left: 0;
		right: 0;
		bottom: 0;
		height: 2px;
		background-color: ${tokens.border.default};
	}
`;

const TrackProgress = styled.div<{ $due?: boolean }>`
	width: 100%;
	height: 3px;
	background-color: ${(props) => (props.$due ? tokens.error.icon : tokens.success.icon)};
`;

const TodayLabel = styled.span`
	position: absolute;
	top: 0;
	right: 0;
	color: ${tokens.text.secondary};
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.small}px;
	font-weight: ${tokens.typography.weights.regular};
	letter-spacing: 0.01em;
	line-height: ${tokens.typography.lineHeights.small};
	white-space: nowrap;
	pointer-events: none;
`;

function ClampedTodayLabel({
	bounds,
	currentDate,
	children,
}: {
	bounds: [Date, Date];
	currentDate: Date;
	children: ReactNode;
}) {
	const rowRef = useRef<HTMLDivElement>(null);
	const labelRef = useRef<HTMLSpanElement>(null);
	const [shift, setShift] = useState(0);

	useLayoutEffect(() => {
		const row = rowRef.current;
		const label = labelRef.current;
		if (!row || !label) {
			return;
		}

		const clamp = () => {
			label.style.transform = 'translateX(50%)';
			const labelRect = label.getBoundingClientRect();
			const rowRect = row.getBoundingClientRect();
			const overflowLeft = rowRect.left - labelRect.left;
			const overflowRight = labelRect.right - rowRect.right;
			const next = overflowLeft > 0 ? overflowLeft : overflowRight > 0 ? -overflowRight : 0;
			const transform = `translateX(calc(50% + ${next}px))`;
			label.style.transform = transform;
			setShift((current) => (current === next ? current : next));
		};

		clamp();
		const observer = new ResizeObserver(clamp);
		observer.observe(row);
		return () => observer.disconnect();
	}, [bounds, currentDate, children]);

	return (
		<TodayLabelRow ref={rowRef}>
			<CycleMomentOffset
				bounds={bounds}
				startDate={bounds[0]}
				endDate={currentDate}
				minWidth={0}
				height="100%"
			>
				<TodayLabel ref={labelRef} style={{ transform: `translateX(calc(50% + ${shift}px))` }}>
					{children}
				</TodayLabel>
			</CycleMomentOffset>
		</TodayLabelRow>
	);
}

const TodayLine = styled.div<{ $height: number; $due?: boolean }>`
	background-color: ${(props) => (props.$due ? tokens.error.icon : tokens.success.icon)};
	height: ${(props) => props.$height}px;
	width: 1px;
	position: absolute;
	right: 0;
	top: 0;
	pointer-events: none;
`;

const HoverDate = styled.div`
	position: relative;
	transform: translateX(-50%) translateY(-10px);
	pointer-events: none;
	color: ${tokens.text.inverse};
	background: ${tokens.surface.sidebar};
	padding: ${tokens.gap.inline}px ${tokens.gap.element}px;
	border-radius: ${tokens.radius.xs}px;
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.small}px;
	font-weight: ${tokens.typography.weights.medium};
	white-space: nowrap;
	width: max-content;
`;

function todayInRange(bounds: [Date, Date], currentDate: Date) {
	const time = toTime(currentDate);
	return time >= toTime(bounds[0]) && time <= toTime(bounds[1]);
}

export function CycleMomentsCard({
	bounds,
	currentDate,
	moments,
	rangeLabel,
	toolbar,
	todayLabel,
	emptyLabel,
	mini = false,
	embedded = false,
	loading,
	allowPagination,
	firstMomentDue,
	formatHoverDate,
	overlay,
}: {
	bounds: [Date, Date];
	currentDate: Date;
	moments: CycleMomentItem[];
	rangeLabel?: string;
	toolbar?: ReactNode;
	todayLabel: string;
	emptyLabel?: string;
	mini?: boolean;
	embedded?: boolean;
	loading?: boolean;
	allowPagination?: boolean;
	firstMomentDue?: Date;
	formatHoverDate?: (date: Date) => string;
	overlay?: ReactNode;
}) {
	const empty = moments.length === 0;
	const showToday = todayInRange(bounds, currentDate);
	const [hoverX, setHoverX] = useState<number | undefined>(undefined);
	const [hoverProgress, setHoverProgress] = useState<number | undefined>(undefined);
	const rangeMs = toTime(bounds[1]) - toTime(bounds[0]);

	const track = (
		<TrackSection>
			{!mini && showToday && (
				<ClampedTodayLabel bounds={bounds} currentDate={currentDate}>
					{todayLabel}
				</ClampedTodayLabel>
			)}
			<Box
				style={{ position: 'relative' }}
				onMouseMove={
					formatHoverDate && !mini
						? (event) => {
								const { left, width } = event.currentTarget.getBoundingClientRect();
								const offsetX = event.clientX - left;
								setHoverX(offsetX);
								setHoverProgress(width ? offsetX / width : 0);
							}
						: undefined
				}
				onMouseLeave={formatHoverDate && !mini ? () => setHoverX(undefined) : undefined}
			>
				<Track>
					<CycleMomentOffset
						bounds={bounds}
						startDate={bounds[0]}
						endDate={currentDate}
						minWidth={0}
						height="100%"
					>
						{showToday && <TrackProgress $due={Boolean(firstMomentDue)} />}
						{!mini && showToday && moments.length > 0 && (
							<TodayLine
								$due={Boolean(firstMomentDue)}
								$height={moments.length * CYCLE_MOMENT_ROW_HEIGHT}
							/>
						)}
					</CycleMomentOffset>
				</Track>
				{hoverX !== undefined && hoverProgress !== undefined && formatHoverDate && (
					<div style={{ position: 'absolute', left: hoverX, top: 0, height: 0 }}>
						<HoverDate>
							{formatHoverDate(new Date(toTime(bounds[0]) + hoverProgress * rangeMs))}
						</HoverDate>
					</div>
				)}
			</Box>
		</TrackSection>
	);

	return (
		<Card $mini={mini} $embedded={embedded}>
			{!mini && !embedded && allowPagination && (
				<FlexGap
					alignItems={['stretch', 'stretch', 'stretch', 'center']}
					flexDirection={['column', 'column', 'column', 'row']}
					flexShrink={0}
					gap={tokens.gap.list}
					justifyContent={['flex-start', 'flex-start', 'flex-start', 'space-between']}
					width="100%"
				>
					{rangeLabel && <Paragraph small>{rangeLabel}</Paragraph>}
					<Flex
						flexWrap="wrap"
						justifyContent={['flex-start', 'flex-start', 'flex-start', 'flex-end']}
						minWidth={0}
						width={['100%', '100%', '100%', 'auto']}
					>
						{toolbar}
					</Flex>
				</FlexGap>
			)}

			{mini ? (
				track
			) : (
				<Timeline>
					{track}
					<Rows $fade={!empty && !loading}>
						{!loading &&
							moments.map((momentItem) => {
								const { clippedStart, clippedEnd } = cycleMomentPlacement(
									bounds,
									momentItem.startDate,
									momentItem.endDate,
								);

								return (
									<MomentRow key={momentItem.id}>
										<CycleMomentOffset
											bounds={bounds}
											startDate={momentItem.startDate}
											endDate={momentItem.endDate}
										>
											<CycleMomentBar
												status={momentItem.status}
												icon={momentItem.icon}
												title={momentItem.title}
												clippedStart={clippedStart}
												clippedEnd={clippedEnd}
											/>
										</CycleMomentOffset>
									</MomentRow>
								);
							})}

						{loading && (
							<Flex py={3} justifyContent="center">
								<Spinner />
							</Flex>
						)}

						{!loading && empty && emptyLabel && (
							<Box py={3}>
								<Paragraph>{emptyLabel}</Paragraph>
							</Box>
						)}

						{overlay && <Overlay>{overlay}</Overlay>}
					</Rows>
				</Timeline>
			)}
		</Card>
	);
}
